import type { CifrasReservas, EstadoReserva } from '../../api/ops/reservas';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';
import type { TarjetaAyuda } from '../../marco/cabecera';

/**
 * Textos de Reservas (8.1 «Reservas del día» y 8.4 «Por completar»), tal cual de la exportación
 * (`diseno/reservas-sala/8.1-8.4-reservas.html`) salvo donde una decisión la corrige:
 * - G14, G15: nada se completa solo; el no-show lo marca siempre el personal.
 * - G121, G797: las cifras de arriba salen de `counts` de listReservations con `service_date` (`counts.by_status`); el
 *   panel elige qué estados suma en cada tarjeta, nunca cuenta la lista.
 * - G159: «Deshacer» 5 s solo en lo que no mueve dinero ni avisa al cliente («Ha llegado», «Vino» y «No vino» sin
 *   cobro); el no-show con cobro va con ventana.
 * - G214: las palabras del negocio (`vocabulary`), sin «mesa» fija.
 * - G318: «Reintentar el cobro» hasta el tercer intento.
 * - G792: al deshacer un no-show se pinta el estado que devuelve la API.
 * - G796: «Imprimir» sin el bloque Plano y sala imprime la lista del día por zona; con él, la hoja de la Sala.
 */
export const PERMISOS = {
    ver: 'reservas.reservas.ver',
    crear: 'reservas.reservas.crear',
    completar: 'reservas.pendientes.completar',
    noShow: 'reservas.no_show.marcar',
    deshacerNoShow: 'reservas.no_show.deshacer',
    llegada: 'pases.puerta.validar',
    pases: 'pases.pases.ver',
    pagosVer: 'pagos.pagos.ver',
    cobrar: 'pagos.cobros.crear',
    clientes: 'clientes.fichas.ver',
    horario: 'disponibilidad.horarios.ver',
    cifras: 'analitica.panel.ver',
} as const;

/**
 * Margen tras la hora de inicio para «Con retraso» en la Sala en vivo (G322: no-show desde inicio + 15 min, como el
 * prototipo). En «Reservas del día» manda `no_show_markable_from` de la reserva (G844: el margen de la regla de reservas).
 */
export const MARGEN_RETRASO_MIN = 15;

/** Intentos del cobro de un no-show (G318). */
export const INTENTOS_COBRO = 3;

/** Estado de la reserva → chip: la misma palabra y el mismo color en todo el panel. */
export const ESTADO: Readonly<Record<EstadoReserva, readonly [string, EstadoChip]>> = {
    pending_payment: ['Pendiente de pago', 'pending'],
    confirmed: ['Confirmada', 'confirmed'],
    arrived: ['Presentada', 'live'],
    pending_completion: ['Por completar', 'pending'],
    completed: ['Completada', 'confirmed'],
    no_show: ['No presentada', 'problem'],
    cancelled: ['Cancelada', 'problem'],
    expired: ['Caducada', 'problem'],
    refunded: ['Reembolsada', 'problem'],
};

/** Las que ya no cuentan (canceladas, caducadas, reembolsadas). */
export const APAGADAS: readonly EstadoReserva[] = ['cancelled', 'expired', 'refunded'];

/** Filtros de la lista del día (RV Filtros). «Por completar» es el apartado 8.4: no se repite como filtro. */
export const FILTROS: readonly { clave: string; etiqueta: string; estados: readonly EstadoReserva[] | null }[] = [
    { clave: 'todas', etiqueta: 'Todas', estados: null },
    { clave: 'por-llegar', etiqueta: 'Por llegar', estados: ['pending_payment', 'confirmed'] },
    { clave: 'presentadas', etiqueta: 'Presentadas', estados: ['arrived', 'completed'] },
    { clave: 'no-presentadas', etiqueta: 'No presentadas', estados: ['no_show'] },
    { clave: 'canceladas', etiqueta: 'Canceladas', estados: APAGADAS },
];

/** Cifra de un filtro con los `counts` de la API (G797): `total` para «Todas» y la suma de sus estados para el resto. */
export function cifraDeFiltro(cifras: CifrasReservas | null | undefined, estados: readonly EstadoReserva[] | null): number | null {
    if (cifras == null) {
        return null;
    }
    return estados === null ? cifras.total : sumaDe(cifras, estados);
}

/** Suma las cifras de la API de esos estados (el panel elige qué enseña; no cuenta reservas). */
export function sumaDe(cifras: CifrasReservas, estados: readonly EstadoReserva[]): number {
    return estados.reduce((n, e) => n + cifras.by_status[e], 0);
}

/** Personas de esos estados (`people_by_status`, G843: las suma la API); null si no llegan. */
export function personasDe(cifras: CifrasReservas, estados: readonly EstadoReserva[]): number | null {
    const p = cifras.people_by_status;
    return p == null ? null : estados.reduce((n, e) => n + (p[e] ?? 0), 0);
}

/** Estados de cada tarjeta de cifra de 8.1. */
export const TARJETAS = {
    reservas: ['pending_payment', 'confirmed', 'arrived', 'pending_completion', 'completed', 'no_show'],
    porLlegar: ['pending_payment', 'confirmed'],
    presentadas: ['arrived', 'completed'],
    porCompletar: ['pending_completion'],
} as const satisfies Record<string, readonly EstadoReserva[]>;

/** Canal de origen (G540). */
export const CANAL: Readonly<Record<string, string>> = {
    panel: 'Panel',
    tablet: 'Tablet de la puerta',
    web: 'Web',
    widget: 'Widget',
    assistant: 'Asistente IA',
    api: 'Integración',
};

const ayuda = (filas: readonly (readonly [NombreIcono, string, string])[]): TarjetaAyuda[] => filas.map(([icono, titulo, texto]) => ({ icono, titulo, texto }));

export const AYUDA = {
    dia: ayuda([
        ['calendar-days', 'Reservas del día', 'Todas las reservas de la jornada, agrupadas por la hora a la que empiezan. Cambia de día arriba.'],
        ['log-in', 'Ha llegado', 'Si el cliente no trae el pase, marca la llegada de un toque. Tienes 5 segundos para deshacerlo.'],
        ['list-checks', 'Nada se completa solo', 'Si una reserva termina sin llegada registrada, queda por completar hasta que alguien del equipo diga si vino.'],
    ]),
    pendientes: ayuda([
        ['list-checks', 'Por completar', 'Reservas que terminaron sin llegada registrada. Mientras quede alguna, hay un aviso en la campana; al cliente no le llega nada.'],
        ['user-x', 'No vino', 'Libera la mesa y, si la política lo prevé, avisamos al cliente y cobramos el no-show sobre su tarjeta de garantía.'],
        ['credit-card', 'Cobros rechazados', 'Si el banco rechaza el cobro de un no-show, se puede reintentar hasta 3 veces.'],
    ]),
    socio: ayuda([['chart-column', 'Cifras del día', 'Ves cuántas reservas y personas hay en cada franja y cuántas han llegado, sin datos de los clientes.']]),
};

/** Cambia «mesa» por la palabra del negocio en un texto de la ayuda (G214). */
export function conPalabra(tarjetas: readonly TarjetaAyuda[], palabra: string): TarjetaAyuda[] {
    return tarjetas.map((t) => ({ ...t, texto: t.texto.replace(/\bla mesa\b/g, conArticulo(palabra)) }));
}

/** «la mesa», «el reservado», «la habitación»: el artículo de la palabra del negocio (G214). */
export function conArticulo(palabra: string): string {
    const p = palabra.toLocaleLowerCase('es');
    return `${/(a|ión|ción)$/.test(p) ? 'la' : 'el'} ${p}`;
}

export const mayuscula = (t: string): string => t.charAt(0).toLocaleUpperCase('es') + t.slice(1);
